import { forwardRef, useEffect } from 'react';
import { FormProvider } from 'react-hook-form';
import { useFormDirtyStore } from '../../../stores/form-dirty-store';
import { useRouteSidePanelStore } from '../../../stores/side-panel-store';
import { type LeaveAlertOption, LeavePageAlert } from '../../leave-alert';

/* ========================================
 * LeavePageAlert 統合用の型定義
 * ======================================== */

type LeaveAlertCondition =
  | {
      /**
       * 追加のアラート条件（カスタムロジック用）
       */
      additionalAlertCondition: boolean;
      dirtyFields?: never;
      isSubmitting?: never;
      isSubmitSuccessful?: never;
    }
  | {
      additionalAlertCondition?: never;
      /**
       * フォームが編集されているか
       */
      dirtyFields: Partial<Readonly<Record<string, unknown>>>;
      /**
       * フォームが送信中か
       */
      isSubmitting: boolean;
      /**
       * フォーム送信が成功したか
       * Submit 成功後に即遷移する場合など
       */
      isSubmitSuccessful: boolean;
    };

type FormProps = {
  /**
   * ページ離脱アラートの条件
   * null を渡すとアラートが無効化される
   * undefined の場合はアラート機能なし
   */
  leavePageAlertCondition:
    | (LeaveAlertCondition & {
        options?: LeaveAlertOption;
      })
    | null;
  children?: React.ReactNode;
} & React.ComponentPropsWithRef<'form'>;

/* ========================================
 * Form コンポーネント本体（LeavePageAlert統合版）
 * ======================================== */

/**
 * React Hook Form + shadcn/ui ベースのフォームコンポーネント
 *
 * LeavePageAlert を統合し、フォーム編集中のページ離脱を防ぎます。
 *
 * @example
 * ```tsx
 * import { useForm } from 'react-hook-form';
 * import { Form } from '@/shared/ui';
 *
 * const form = useForm({
 *   defaultValues: { name: '' },
 * });
 *
 * <FormProvider {...form}>
 *   <Form
 *     leavePageAlertCondition={{
 *       dirtyFields: form.formState.dirtyFields,
 *       isSubmitting: form.formState.isSubmitting,
 *       isSubmitSuccessful: form.formState.isSubmitSuccessful,
 *     }}
 *     onSubmit={form.handleSubmit(onSubmit)}
 *   >
 *     <FormField
 *       control={form.control}
 *       name="name"
 *       render={({ field }) => (
 *         <FormItem>
 *           <FormLabel>名前</FormLabel>
 *           <FormControl>
 *             <Input {...field} />
 *           </FormControl>
 *           <FormMessage />
 *         </FormItem>
 *       )}
 *     />
 *   </Form>
 * </FormProvider>
 * ```
 */
const FormComponent = forwardRef<HTMLFormElement, FormProps>(
  ({ leavePageAlertCondition, children, ...props }, ref) => {
    const { dirtyFields, isSubmitting, isSubmitSuccessful, additionalAlertCondition } =
      leavePageAlertCondition ?? {};

    // dirtyFields を使用して実際にユーザーが変更したかを判定
    // isDirty は defaultValues と現在値の比較で、未設定フィールドが空文字列に変換されると true になる問題がある
    const isDirty = dirtyFields ? Object.keys(dirtyFields).length > 0 : false;

    const { setIsDirty: setFormDirty } = useFormDirtyStore();
    const isRightPanelOpen = useRouteSidePanelStore((s) => s.isOpen);
    const setSidePanelHasUnsavedChanges = useRouteSidePanelStore((s) => s.setHasUnsavedChanges);

    useEffect(() => {
      // undefinedチェックが必要な理由：
      // - isDirtyやadditionalAlertConditionがfalseの場合も意図的な状態として扱う必要がある
      // - false（変更なし）の場合もsetHasUnsavedChanges(false)を呼んで離脱アラートを解除する必要がある
      // - !isDirtyだとfalseの時に処理がスキップされ、離脱アラートの状態が更新されない

      if (isDirty !== undefined || additionalAlertCondition !== undefined) {
        const computedDirty = !!(
          (isDirty && !isSubmitting && !isSubmitSuccessful) ||
          additionalAlertCondition
        );

        // ページ離脱アラート用（LeavePageAlert の useBlocker で使用）
        setFormDirty(computedDirty);

        // サイドパネルが開いている場合は、サイドパネルの閉じる/モード変更ガードにも伝播
        if (isRightPanelOpen) {
          setSidePanelHasUnsavedChanges(computedDirty);
        }
      }
    }, [
      isDirty,
      isSubmitting,
      isSubmitSuccessful,
      setFormDirty,
      additionalAlertCondition,
      isRightPanelOpen,
      setSidePanelHasUnsavedChanges,
    ]);

    // アンマウント時のみフォームのダーティ状態をリセット
    // サイドパネル内のFormがアンマウントされた時にisDirty=trueが残るのを防ぐ
    // 依存配列変化時のクリーンアップとは分離し、メインフォームの状態を誤ってリセットしない
    useEffect(() => {
      return () => {
        setFormDirty(false);
      };
    }, [setFormDirty]);

    return (
      <>
        <form ref={ref} {...props}>
          {children}
        </form>
        {leavePageAlertCondition && !isRightPanelOpen && (
          <LeavePageAlert options={leavePageAlertCondition.options} />
        )}
      </>
    );
  },
);
FormComponent.displayName = 'Form';

/* ========================================
 * エクスポート（Object.assign で静的プロパティを追加）
 * ======================================== */

/**
 * フォームコンポーネント（shadcn/ui + LeavePageAlert 統合版）
 *
 * `Form.Provider` として FormProvider を、
 * `Form.Field`, `Form.Item` などの子コンポーネントを含みます。
 *
 * @example
 * ```tsx
 * <Form.Provider {...form}>
 *   <Form leavePageAlertCondition={...} onSubmit={...}>
 *     <Form.Field control={form.control} name="name" render={...} />
 *   </Form>
 * </Form.Provider>
 * ```
 */
export const Form = Object.assign(FormComponent, {
  Provider: FormProvider,
});
